import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    FormControlLabel,
    Checkbox,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";
import SRCards from "../../components/SRCards";
import SelectCompany from "../../components/SelectCompany";
import CommonServiceApi from "../../services/common-service";

type PaymentTerm = {
    id?: number;
    name?: string;
    termName?: string;
    paymentTermName?: string;
    displayName?: string;
    description?: string;
    dueDays?: number;
    stdDueDays?: number;
    days?: number;
    dueInDays?: number;
    paymentTermDays?: number;
    netDueDays?: number;
    discountDays?: number;
    discountTermDays?: number;
    stdDiscountDays?: number;
    discountPct?: string;
    isDefault?: boolean;
    isActive?: boolean;
    isDeleted?: boolean;
    companyId?: number;
    [key: string]: any;
};

type SnackbarSeverity = "success" | "error" | "info" | "warning";

const useStyles = makeStyles({
    container: {
        marginTop: "24px",
        marginBottom: "24px",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "16px",
        marginBottom: "16px",
        gap: "12px",
        flexWrap: "wrap",
    },
    tableWrapper: {
        marginTop: "12px",
    },
    actionsCell: {
        whiteSpace: "nowrap",
    },
});

const defaultFormState = {
    presetKey: "",
    name: "",
    dueDays: "",
    stdDiscountDays: "",
    discountPct: "",
    companyId: "",
    isActive: true,
    isDefault: false,
};

const PAYMENT_TERM_PRESETS = [
    { key: "net_10", label: "Net 10", name: "Net 10", stdDueDays: 10 },
    { key: "net_15", label: "Net 15", name: "Net 15", stdDueDays: 15 },
    { key: "net_20", label: "Net 20", name: "Net 20", stdDueDays: 20 },
    { key: "net_25", label: "Net 25", name: "Net 25", stdDueDays: 25 },
    { key: "net_30", label: "Net 30", name: "Net 30", stdDueDays: 30 },
    { key: "net_45", label: "Net 45", name: "Net 45", stdDueDays: 45 },
    { key: "net_60", label: "Net 60", name: "Net 60", stdDueDays: 60 },
    { key: "net_75", label: "Net 75", name: "Net 75", stdDueDays: 75 },
    { key: "net_90", label: "Net 90", name: "Net 90", stdDueDays: 90 },
    {
        key: "due_on_receipt",
        label: "Due on receipt",
        name: "Due on receipt",
        stdDueDays: 0,
    },
];

const getFirstDefined = (term: PaymentTerm, keys: string[]) => {
    for (const key of keys) {
        if (term[key] !== undefined && term[key] !== null && term[key] !== "") {
            return term[key];
        }
    }
    return "";
};

const getTermName = (term: PaymentTerm) => {
    return String(
        getFirstDefined(term, [
            "name",
            "termName",
            "paymentTermName",
            "displayName",
            "description",
        ]) || ""
    );
};

const getTermDays = (term: PaymentTerm) => {
    return String(
        getFirstDefined(term, [
            "stdDueDays",
            "dueDays",
            "days",
            "dueInDays",
            "paymentTermDays",
            "netDueDays",
        ]) || ""
    );
};

const getPresetKeyFromTerm = (term: PaymentTerm) => {
    const termDays = Number(getTermDays(term));
    if (!Number.isNaN(termDays)) {
        const byDays = PAYMENT_TERM_PRESETS.find(
            (preset) => preset.stdDueDays === termDays
        );
        if (byDays) {
            return byDays.key;
        }
    }
    const termName = getTermName(term).toLowerCase();
    const byName = PAYMENT_TERM_PRESETS.find(
        (preset) =>
            preset.name.toLowerCase() === termName ||
            preset.label.toLowerCase() === termName
    );
    return byName ? byName.key : "";
};

const PaymentTerms = () => {
    const classes = useStyles();
    const apiservices = useMemo(() => new CommonServiceApi(), []);

    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
    const [isPaymentTermEnabled, setIsPaymentTermEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState<PaymentTerm | null>(null);
    const [formState, setFormState] = useState({ ...defaultFormState });
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>(
        {}
    );
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: SnackbarSeverity;
    }>({ open: false, message: "", severity: "success" });

    const companyId = selectedCompany?.value
        ? Number(selectedCompany.value)
        : 0;

    const showSnackbar = (message: string, severity: SnackbarSeverity) => {
        setSnackbar({ open: true, message, severity });
    };

    const normalizeNamePayload = (
        payload: Record<string, any>,
        term: PaymentTerm | null,
        name: string
    ) => {
        const keys = [
            "name",
            "termName",
            "paymentTermName",
            "displayName",
        ];
        const available = term
            ? keys.filter((key) =>
                  Object.prototype.hasOwnProperty.call(term, key)
              )
            : [];
        if (available.length === 0) {
            payload.name = name;
            return;
        }
        available.forEach((key) => {
            payload[key] = name;
        });
    };

    const normalizeDaysPayload = (
        payload: Record<string, any>,
        term: PaymentTerm | null,
        value: number
    ) => {
        const keys = [
            "stdDueDays",
            "dueDays",
            "days",
            "dueInDays",
            "paymentTermDays",
            "netDueDays",
        ];
        const available = term
            ? keys.filter((key) =>
                  Object.prototype.hasOwnProperty.call(term, key)
              )
            : [];
        if (available.length === 0) {
            payload.stdDueDays = value;
            return;
        }
        available.forEach((key) => {
            payload[key] = value;
        });
    };

    const normalizeDiscountDaysPayload = (
        payload: Record<string, any>,
        term: PaymentTerm | null,
        value: number
    ) => {
        const keys = ["stdDiscountDays", "discountDays", "discountTermDays"];
        const available = term
            ? keys.filter((key) =>
                  Object.prototype.hasOwnProperty.call(term, key)
              )
            : [];
        if (available.length === 0) {
            payload.stdDiscountDays = value;
            return;
        }
        available.forEach((key) => {
            payload[key] = value;
        });
    };

    const normalizeDiscountPctPayload = (
        payload: Record<string, any>,
        term: PaymentTerm | null,
        value: string
    ) => {
        const keys = ["discountPct", "discountPercent", "discountPercentage"];
        const available = term
            ? keys.filter((key) =>
                  Object.prototype.hasOwnProperty.call(term, key)
              )
            : [];
        if (available.length === 0) {
            payload.discountPct = value;
            return;
        }
        available.forEach((key) => {
            payload[key] = value;
        });
    };

    const buildPayload = (term: PaymentTerm | null) => {
        const payload: Record<string, any> = {};
        if (term?.id !== undefined) {
            payload.id = term.id;
        }

        const name = formState.name.trim();
        if (name) {
            normalizeNamePayload(payload, term, name);
        }

        const dueDaysValue = formState.dueDays.trim();
        if (dueDaysValue !== "") {
            const dueDays = Number(dueDaysValue);
            if (!Number.isNaN(dueDays)) {
                normalizeDaysPayload(payload, term, dueDays);
            }
        }

        const stdDiscountDaysValue = formState.stdDiscountDays.trim();
        if (stdDiscountDaysValue !== "") {
            const stdDiscountDays = Number(stdDiscountDaysValue);
            if (!Number.isNaN(stdDiscountDays)) {
                normalizeDiscountDaysPayload(payload, term, stdDiscountDays);
            }
        }

        const discountPctValue = formState.discountPct.trim();
        if (discountPctValue !== "") {
            normalizeDiscountPctPayload(payload, term, discountPctValue);
        }

        payload.isActive = formState.isActive;
        payload.isDefault = formState.isDefault;
        const formCompanyId = Number(formState.companyId);
        if (!Number.isNaN(formCompanyId) && formCompanyId > 0) {
            payload.companyId = formCompanyId;
        } else if (companyId) {
            payload.companyId = companyId;
        }
        if (term?.companyId) {
            payload.companyId = term.companyId;
        }

        return payload;
    };

    const validateForm = () => {
        const errors: { [key: string]: string } = {};
        if (!formState.presetKey) {
            errors.presetKey = "Payment term is required.";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const fetchPaymentTerms = useCallback(async () => {
        if (!selectedCompany?.value) {
            setPaymentTerms([]);
            return;
        }
        setLoading(true);
        try {
            const result = await apiservices.getCompanyPaymentTerms();
            const list = Array.isArray(result)
                ? result
                : result?.data || [];
            setPaymentTerms(list || []);
        } catch (error: any) {
            console.error(error);
            showSnackbar("Failed to load payment terms.", "error");
            setPaymentTerms([]);
        } finally {
            setLoading(false);
        }
    }, [apiservices, selectedCompany]);

    useEffect(() => {
        fetchPaymentTerms();
    }, [fetchPaymentTerms]);

    useEffect(() => {
        const fetchEnabledFeatures = async () => {
            if (!selectedCompany?.value) {
                setIsPaymentTermEnabled(false);
                return;
            }
            try {
                const result: any = await apiservices.getEnabledFeatures({
                    company: selectedCompany.value,
                });
                const data = typeof result === 'string' ? JSON.parse(result) : result;
                const enabledFeatures: string[] = data?.enabledFeatures || [];
                setIsPaymentTermEnabled(enabledFeatures.includes('PAY_TERM_FIELD'));
            } catch {
                setIsPaymentTermEnabled(false);
            }
        };
        fetchEnabledFeatures();
    }, [selectedCompany, apiservices]);

    const handleEnablePaymentTerm = async () => {
        setLoading(true);
        try {
            const result: any = await apiservices.featureUpdating({
                featuresToEnable: ['PAY_TERM_FIELD'],
                featuresToDisable: [],
                meta: {},
            });
            if (result?.settingsUpdatedList?.length > 0) {
                await apiservices.rebuildUpdatedSettingsRedisCache(
                    result.settingsUpdatedList,
                    companyId
                );
            }
            setIsPaymentTermEnabled(true);
            showSnackbar('Payment term feature enabled successfully.', 'success');
            fetchPaymentTerms();
        } catch {
            showSnackbar('Failed to enable payment term feature.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetFormState = () => {
        setFormState({
            ...defaultFormState,
            companyId: companyId ? String(companyId) : "",
        });
        setFormErrors({});
    };

    const handlePresetChange = (
        event: React.ChangeEvent<{ value: unknown }>
    ) => {
        const value = String(event.target.value || "");
        const preset = PAYMENT_TERM_PRESETS.find(
            (item) => item.key === value
        );
        setFormState((prev) => ({
            ...prev,
            presetKey: value,
            name: preset ? preset.name : "",
            dueDays: preset ? String(preset.stdDueDays) : "",
        }));
        setFormErrors((prev) => ({ ...prev, presetKey: "" }));
    };

    const handleAddClick = () => {
        setIsEditMode(false);
        setSelectedTerm(null);
        resetFormState();
        setDialogOpen(true);
    };

    const handleEditClick = (term: PaymentTerm) => {
        setIsEditMode(true);
        setSelectedTerm(term);
        setFormState({
            presetKey: getPresetKeyFromTerm(term),
            name: getTermName(term),
            dueDays: getTermDays(term),
            stdDiscountDays: String(
                getFirstDefined(term, [
                    "stdDiscountDays",
                    "discountDays",
                    "discountTermDays",
                ]) || ""
            ),
            discountPct: String(
                getFirstDefined(term, [
                    "discountPct",
                    "discountPercent",
                    "discountPercentage",
                ]) || ""
            ),
            companyId: String(term.companyId || companyId || ""),
            isActive: term.isActive !== undefined ? term.isActive : true,
            isDefault: term.isDefault === true,
        });
        setFormErrors({});
        setDialogOpen(true);
    };

    const clearOtherDefaults = async () => {
        const currentId = selectedTerm?.id;
        const defaultTerms = paymentTerms.filter(
            (term) => term.isDefault && term.id !== currentId
        );
        if (defaultTerms.length === 0) {
            return;
        }
        await Promise.all(
            defaultTerms.map((term) =>
                apiservices.updatePaymentTerm({
                    id: term.id,
                    companyId: term.companyId || companyId,
                    isDefault: false,
                })
            )
        );
    };

    const handleDeleteClick = (term: PaymentTerm) => {
        setSelectedTerm(term);
        setDeleteDialogOpen(true);
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }
        const payload = buildPayload(isEditMode ? selectedTerm : null);
        setLoading(true);
        try {
            if (formState.isDefault) {
                await clearOtherDefaults();
            }
            if (isEditMode) {
                await apiservices.updatePaymentTerm(payload);
                showSnackbar("Payment term updated successfully.", "success");
            } else {
                await apiservices.addPaymentTermWithoutQB(payload);
                showSnackbar("Payment term added successfully.", "success");
            }
            setDialogOpen(false);
            resetFormState();
            fetchPaymentTerms();
        } catch (error: any) {
            console.error(error);
            showSnackbar("Failed to save payment term.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedTerm) {
            setDeleteDialogOpen(false);
            return;
        }
        setLoading(true);
        try {
            const payload = {
                id: selectedTerm.id,
                companyId: selectedTerm.companyId || companyId,
                isDeleted: true,
                isActive: false,
            };
            await apiservices.updatePaymentTerm(payload);
            showSnackbar("Payment term deleted successfully.", "success");
            setDeleteDialogOpen(false);
            fetchPaymentTerms();
        } catch (error: any) {
            console.error(error);
            showSnackbar("Failed to delete payment term.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SRCards />
            <Container className={classes.container}>
                <Typography variant="h5" gutterBottom>
                    Payment Terms
                </Typography>
                <SelectCompany optionHandler={setSelectedCompany} />
                {loading && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {companyId && !loading && !isPaymentTermEnabled ? (
                    <Box mt={4} display="flex" flexDirection="column" alignItems="center" style={{ gap: '16px' }}>
                        <Typography variant="body1">
                            Payment terms are not enabled for this company. You can enable it from the Feature Updating module or click below to enable it now.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEnablePaymentTerm}
                        >
                            Enable Payment Terms
                        </Button>
                    </Box>
                ): null}
                {companyId && !loading && isPaymentTermEnabled ? (
                    <>
                        <Box className={classes.headerRow}>
                            <Typography variant="body2">
                                {localStorage.getItem("selectedCompanyDisplay") || ""}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleAddClick}
                            >
                                Add Payment Term
                            </Button>
                        </Box>
                        <TableContainer
                            component={Paper}
                            className={classes.tableWrapper}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Due Days</TableCell>
                                        <TableCell>Company Id</TableCell>
                                        <TableCell>Active</TableCell>
                                        <TableCell>Default</TableCell>
                                        <TableCell>Deleted</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paymentTerms.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                No payment terms found.
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                    {paymentTerms.map((term) => (
                                        <TableRow key={term.id || getTermName(term)}>
                                            <TableCell>{getTermName(term)}</TableCell>
                                            <TableCell>{getTermDays(term)}</TableCell>
                                            <TableCell>{term.companyId || ""}</TableCell>
                                            <TableCell>
                                                {term.isActive ? "Yes" : "No"}
                                            </TableCell>
                                            <TableCell>
                                                {term.isDefault ? "Yes" : "No"}
                                            </TableCell>
                                            <TableCell>
                                                {term.isDeleted ? "Yes" : "No"}
                                            </TableCell>
                                            <TableCell className={classes.actionsCell}>
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleEditClick(term)}
                                                >
                                                    Update
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="secondary"
                                                    disabled={term.isDeleted}
                                                    onClick={() => handleDeleteClick(term)}
                                                >
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                ): null}
            </Container>

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                aria-labelledby="payment-term-dialog-title"
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle id="payment-term-dialog-title">
                    {isEditMode ? "Update Payment Term" : "Add Payment Term"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Company Id"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={formState.companyId}
                        disabled
                    />
                    <FormControl
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        error={Boolean(formErrors.presetKey)}
                    >
                        <InputLabel id="payment-term-preset-label">
                            Payment Term
                        </InputLabel>
                        <Select
                            labelId="payment-term-preset-label"
                            value={formState.presetKey}
                            onChange={handlePresetChange}
                            label="Payment Term"
                            disabled={isEditMode}
                        >
                            <MenuItem value="">
                                <em>Select a payment term</em>
                            </MenuItem>
                            {PAYMENT_TERM_PRESETS.map((preset) => (
                                <MenuItem key={preset.key} value={preset.key}>
                                    {preset.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {formErrors.presetKey && (
                            <FormHelperText>
                                {formErrors.presetKey}
                            </FormHelperText>
                        )}
                    </FormControl>
                    <TextField
                        label="Std Due Days"
                        type="number"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={formState.dueDays}
                        disabled
                    />
                    <TextField
                        label="Std Discount Days (optional)"
                        type="number"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={formState.stdDiscountDays}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                stdDiscountDays: event.target.value,
                            }))
                        }
                    />
                    <TextField
                        label="Discount Pct (optional)"
                        type="number"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={formState.discountPct}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                discountPct: event.target.value,
                            }))
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formState.isActive}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        isActive: event.target.checked,
                                    }))
                                }
                            />
                        }
                        label="Active"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formState.isDefault}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        isDefault: event.target.checked,
                                    }))
                                }
                            />
                        }
                        label="Default"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDialogOpen(false);
                            resetFormState();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleSave}>
                        {isEditMode ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="payment-term-delete-title"
            >
                <DialogTitle id="payment-term-delete-title">
                    Delete Payment Term
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to delete{" "}
                    <strong>{getTermName(selectedTerm || {})}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="secondary" onClick={handleDeleteConfirm}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() =>
                    setSnackbar((prev) => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() =>
                        setSnackbar((prev) => ({ ...prev, open: false }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PaymentTerms;
